import Select from '../../../components/Select';
import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { Input } from '../../../components/Input';
import Datepicker from 'react-tailwindcss-datepicker';
import { Button } from '../../../components/Button';
import magnifyingGlass from '@/assets/images/common/magnifying-glass.png';
import {
    formatDateForServer,
    getInitialDate,
    createIndividualCalendarOptions,
    getDefaultIndividualCalendarOption,
    getDatepickerShortcuts,
    IndividualCalendarItem,
    CalendarOption,
    SearchParams,
    ColorOption,
    getDefaultColorOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
    ShortcutLabels,
    isHolidayOrBirthdayCalendar,
} from './util/Search';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { LocalStorageKeys } from '@/constants/localStorage';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/hooks/UserContext';
import { buildLanguageTagFromUser } from '@/utils/locale';
import { resolveWeekStart } from '@/i18n/weekStart';
import Modal from '@/components/Modal';
import { ExternalLinkIcon } from '@/components/icons/ExternalLinkIcon';
import { PeriodLockedModal } from '@/components/PeriodLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import { getDateRangeForPeriod } from '@/utils/time';

// プロップス
interface SearchProps {
    handleSearch?: (
        searchCalendar: CalendarOption,
        startTime: string,
        endTime: string,
        searchKeyword: string,
        searchColor: ColorOption,
    ) => void;
    calendarList?: IndividualCalendarItem[]; // 個別カレンダーリストに変更
    colorOptions?: ColorOption[];
    // 初期値および更新後の値保持のための追加プロパティ
    initialSearchParams?: SearchParams;
}

interface PlanLockedModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGoPlans: () => void;
}

function PlanLockedModal({ isOpen, onClose, onGoPlans }: PlanLockedModalProps) {
    const { t } = useTranslation('analysis');
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            contentClassName="w-full max-w-md p-6"
        >
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center">
                    {t('lockedModal.title')}
                </h3>
                <div
                    className="text-sm text-gray-700 [&_strong]:font-semibold [&_strong]:block [&_strong]:mt-3 [&_strong]:mb-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:mt-2 [&_ul]:mb-2 [&_ul]:pl-4 [&_li]:mb-1 [&_li]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: t('lockedModal.message') }}
                />
                <div className="flex justify-center mt-4">
                    <button
                        type="button"
                        className="px-6 py-2 rounded-md bg-kotomilThemaColor text-black hover:bg-kotomilThemaColor/90 text-sm inline-flex items-center gap-2"
                        onClick={() => {
                            onClose();
                            onGoPlans();
                        }}
                    >
                        <span>{t('lockedModal.cta')}</span>
                        <ExternalLinkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// コンポーネント
const Search = ({ handleSearch, calendarList, initialSearchParams }: SearchProps) => {
    const { t, i18n } = useTranslation('search');
    const { userInfo } = useUser();

    const localizeColorOption = useCallback(
        (option?: ColorOption | null): ColorOption => {
            if (!option) {
                const base = getDefaultColorOption();
                return { ...base, label: t('color.none') };
            }
            if (option.isDivider) {
                return option;
            }
            if (option.value === '') {
                return {
                    ...option,
                    label: t('color.none'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            if (option.value === 'all') {
                return {
                    ...option,
                    label: t('color.all'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            return {
                ...option,
                originalLabel: option.originalLabel ?? option.label,
            };
        },
        [t],
    );

    const shortcutLabels = t('shortcuts', { returnObjects: true }) as ShortcutLabels;

    const languageTag = buildLanguageTagFromUser(userInfo, i18n.language);
    const datepickerLocale = languageTag.startsWith('ja') ? 'ja' : 'en';
    const weekStart = resolveWeekStart(userInfo?.weekStart);
    const isPaidPlan = Boolean(userInfo?.isPaid);


    // 初期キーワード
    const [searchKeyword, setSearchKeyword] = useState(initialSearchParams?.keyword || '');
    const [searchCalendar, setSearchCalendar] = useState<CalendarOption>({
        value: '',
        label: '',
    });
    // 表示用色オプション（並び順・仕切り付き）
    const [colorOptionsView, setColorOptionsView] = useState<ColorOption[]>([]);
    // プランロック用モーダル
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

    // 個別カレンダーリストからオプションを作成（祝日カレンダー除外）
    // 有料プランでない場合はメインカレンダー以外をロック
    const calendarOptions = useMemo(
        () => createIndividualCalendarOptions(calendarList, { lockNonDefault: !isPaidPlan }),
        [calendarList, isPaidPlan],
    );

    // カレンダー選択のドロップダウン幅を計算（最長の項目に合わせる）
    const [calendarDropdownWidth, setCalendarDropdownWidth] = useState<React.CSSProperties>({});

    // 実際のDOM要素で幅を測定
    useEffect(() => {
        if (!calendarOptions || calendarOptions.length === 0) {
            setCalendarDropdownWidth({});
            return;
        }

        // 一時的な要素を作成して幅を測定（最大幅の制限はかけない）
        const measureElement = document.createElement('div');
        measureElement.style.position = 'absolute';
        measureElement.style.visibility = 'hidden';
        measureElement.style.whiteSpace = 'nowrap';
        measureElement.style.fontSize = '0.875rem'; // text-baseと同じ
        measureElement.style.fontFamily = getComputedStyle(document.body).fontFamily;
        measureElement.style.padding = '8px 16px'; // px-4 py-2と同じ
        document.body.appendChild(measureElement);

        let maxWidth = 0;
        calendarOptions.forEach((opt) => {
            // ロックアイコンがある場合はテキストの後に追加
            const text = opt.isLocked ? `${opt.label} 🔒` : opt.label;
            measureElement.textContent = text;
            const width = measureElement.offsetWidth;
            if (width > maxWidth) {
                maxWidth = width;
            }
        });

        document.body.removeChild(measureElement);

        // 計測した最長幅をそのまま使用（最大値の制限なし）
        setCalendarDropdownWidth({
            width: `${maxWidth}px`,
        });
    }, [calendarOptions]);

    // 祝日・誕生日カレンダーを除外したカレンダーリスト
    const filteredCalendarList = useMemo(() => {
        return calendarList?.filter((cal) => !isHolidayOrBirthdayCalendar(cal)) || [];
    }, [calendarList]);

    // カレンダーが1つだけかどうか
    const isSingleCalendar = filteredCalendarList.length === 1;

    // デフォルトカレンダー（is_default=true）を取得
    const defaultCalendarOption = useMemo(
        () => getDefaultIndividualCalendarOption(calendarList),
        [calendarList],
    );

    const defaultColorOption = useMemo(
        () => localizeColorOption(getDefaultColorOption()),
        [localizeColorOption],
    );

    // 色選択
    const [searchColor, setSearchColor] = useState<ColorOption>(() =>
        localizeColorOption(initialSearchParams?.color || getDefaultColorOption()),
    );

    // 検索期間の状態
    const [searchPeriod, setSearchPeriod] = useState(() =>
        getInitialDate(initialSearchParams, { weekStartsOn: weekStart }),
    );

    // 検索期間のISO文字列を取得
    const startDate: string = searchPeriod?.startDate
        ? formatDateForServer(searchPeriod.startDate)
        : '';
    const endDate: string = searchPeriod?.endDate ? formatDateForServer(searchPeriod.endDate) : '';
    const [isFirstSearch, setIsFirstSearch] = useState(true);

    // Datepicker の入力要素への参照（iOSのキーボード抑止用）
    const datepickerContainerRef = useRef<HTMLSpanElement>(null);

    // iOSでのキーボード表示を抑止（readonly + inputmode=none を付与）
    useEffect(() => {
        const container = datepickerContainerRef.current;
        if (!container) return;
        const input = container.querySelector('input');
        if (!input) return;
        input.setAttribute('readonly', 'true');
        input.setAttribute('inputmode', 'none');
        input.setAttribute('autocomplete', 'off');
    }, []);

    /**
     * カレンダー選択のラベルをロケールに合わせて更新
     */
    useEffect(() => {
        if (!calendarOptions.length) return;
        setSearchCalendar((prev) => {
            if (!prev.value) {
                return defaultCalendarOption;
            }
            const matched = calendarOptions.find((option) => option.value === prev.value);
            return matched ?? prev;
        });
    }, [calendarOptions, defaultCalendarOption]);

    /**
     * 初期値設定（前回の検索パラメータを復元）
     */
    useEffect(() => {
        if (initialSearchParams) {
            // キーワードがある場合は設定
            if (initialSearchParams.keyword) {
                setSearchKeyword(initialSearchParams.keyword);
            }

            // 日付範囲がある場合は設定
            if (initialSearchParams.startDate && initialSearchParams.endDate) {
                setSearchPeriod({
                    startDate: new Date(initialSearchParams.startDate),
                    endDate: new Date(initialSearchParams.endDate),
                });
                setIsFirstSearch(false);
            }

            // 色選択（初期値がある場合は設定）
            if (initialSearchParams.color) {
                setSearchColor(localizeColorOption(initialSearchParams.color));
            } else {
                setSearchColor(defaultColorOption);
            }

            // カレンダー選択（初期値がある場合は設定）
            if (initialSearchParams.calendar) {
                setSearchCalendar(initialSearchParams.calendar);
            }
        }
    }, [defaultColorOption, initialSearchParams, localizeColorOption]);

    useEffect(() => {
        if (initialSearchParams?.startDate && initialSearchParams?.endDate) {
            return;
        }
        if (!isFirstSearch) {
            return;
        }
        setSearchPeriod(getInitialDate(undefined, { weekStartsOn: weekStart }));
    }, [initialSearchParams, isFirstSearch, weekStart]);

    /**
     * 色オプションの描画順を更新
     */
    const refreshColorOptions = useCallback(
        async (calendarId: string) => {
            // 個別カレンダーID（user_calendarsテーブルのid）からカレンダー情報を取得
            const selected = calendarList?.find((c) => String(c.id) === calendarId);
            const providerName = selected?.providerName || '';
            const base = await getBaseColorOptionsByProvider(providerName, languageTag);
            const userIdValue = userInfo?.userId || '';
            // LocalStorageのキーはカレンダーIDベースに変更
            const key = `${LocalStorageKeys.COLOR_ORDER}:${calendarId}`;
            let order: string[] = [];
            const saved = await getLocalStorage(userIdValue, key);
            if (saved) {
                try {
                    order = JSON.parse(saved);
                } catch {
                    order = [];
                }
            }
            const ordered = applyColorOrderWithDividers(base, order).map(localizeColorOption);
            setColorOptionsView(ordered);
        },
        [calendarList, localizeColorOption, userInfo?.userId, languageTag],
    );

    // カレンダー変更時に色リストを並べ替え
    useEffect(() => {
        if (searchCalendar?.value) {
            refreshColorOptions(searchCalendar.value);
        }
    }, [refreshColorOptions, searchCalendar]);

    useEffect(() => {
        setColorOptionsView((prev) => prev.map((option) => localizeColorOption(option)));
        setSearchColor((prev) => localizeColorOption(prev));
    }, [localizeColorOption]);

    const selectedCalendarOption = useMemo(() => {
        const match = calendarOptions.find((option) => option.value === searchCalendar.value);
        if (match) return match;
        if (searchCalendar.value) return searchCalendar;
        return defaultCalendarOption;
    }, [calendarOptions, defaultCalendarOption, searchCalendar]);

    const selectedColorOption = useMemo(() => {
        const match = colorOptionsView.find((option) => option.value === searchColor.value);
        if (match) return match;
        if (searchColor) return searchColor;
        return defaultColorOption;
    }, [colorOptionsView, defaultColorOption, searchColor]);

    const [isPeriodLockedModalOpen, setIsPeriodLockedModalOpen] = useState(false);
    const [selectedPeriodType, setSelectedPeriodType] = useState<'3months' | '6months' | '12months' | null>(null);
    const isPaid = Boolean(userInfo?.isPaid);

    /**
     * 選択された期間が有料プラン用の期間かどうかを判定し、期間タイプを返す
     */
    const getPaidPlanPeriodType = useCallback(
        (startDate: Date | null, endDate: Date | null): '3months' | '6months' | '12months' | null => {
            if (!startDate || !endDate) return null;

            const paidPeriods: Array<{ type: '3months' | '6months' | '12months'; periodType: string }> = [
                { type: '3months', periodType: 'PAST_3_MONTHS' },
                { type: '6months', periodType: 'PAST_6_MONTHS' },
                { type: '12months', periodType: 'PAST_12_MONTHS' },
            ];
            for (const { type, periodType } of paidPeriods) {
                const { start, end } = getDateRangeForPeriod(periodType, {
                    weekStartsOn: weekStart,
                });
                // 日付を比較（時刻部分を無視）
                const normalizeDate = (d: Date) => {
                    const normalized = new Date(d);
                    normalized.setHours(0, 0, 0, 0);
                    return normalized;
                };
                const normalizedStart = normalizeDate(start);
                const normalizedEnd = normalizeDate(end);
                const normalizedSelectedStart = normalizeDate(startDate);
                const normalizedSelectedEnd = normalizeDate(endDate);

                if (
                    normalizedStart.getTime() === normalizedSelectedStart.getTime() &&
                    normalizedEnd.getTime() === normalizedSelectedEnd.getTime()
                ) {
                    return type;
                }
            }
            return null;
        },
        [weekStart],
    );

    /**
     * 更新ボタン押下
     */
    const handleSubmit = () => {
        const isAllColor = searchColor?.value === 'all';
        const effectiveKeyword = isAllColor ? '' : searchKeyword;

        // カレンダーが1つだけの場合は自動的にそのカレンダーを使用
        const calendarToUse = isSingleCalendar && filteredCalendarList[0]
            ? { value: String(filteredCalendarList[0].id), label: filteredCalendarList[0].calendarName }
            : searchCalendar;

        // 検索処理（親の関数実行）
        // イベントログは Analysis.tsx の handleSearch 内で送信される
        handleSearch?.(
            calendarToUse, // カレンダー
            startDate, // 開始日
            endDate, // 終了日
            effectiveKeyword, // キーワード（色=すべて の場合は未使用）
            searchColor, // 色（暫定：フロント側のみ）
        );
    };

    /**
     * レンダリング
     */
    return (
        <Fragment>
        <div className="flex flex-col lg:flex-row w-full items-center lg:items-center gap-6 px-4">
            {/* 左側: 入力群（折り返し可） */}
            <div className="flex flex-wrap items-center gap-6 flex-1 min-w-0 w-full">
                {/* カレンダー選択 */}
                    {/* 祝日カレンダーを除外してカレンダーが1つだけの場合は非表示 */}
                    {!isSingleCalendar && (
                <div className="flex flex-wrap items-center">
                    <p className="text-gray-500 shrink-0 mb-0 lg:mb-0 mr-2 pb-1 min-w-[100px] lg:min-w-[10px]">
                        {t('labels.calendar')}
                    </p>
                    <Select
                        options={calendarOptions}
                        defaultValue={selectedCalendarOption}
                        onValueChange={(option) => {
                                    if (option.isLocked) {
                                        setIsPlanModalOpen(true);
                                        return;
                                    }
                            setSearchCalendar(option);
                            // カレンダー変更時は色選択を初期化
                            setSearchColor(defaultColorOption);
                        }}
                        className="text-base w-full min-w-[250px]"
                                dropdownStyle={calendarDropdownWidth}
                                noWrap={true}
                    />
                </div>
                    )}
                {/* 期間指定 */}
                <div className="flex flex-wrap items-center">
                    <span className="text-gray-500 shrink-0 mb-0 lg:mb-0 mr-2 pb-1 min-w-[100px] lg:min-w-[10px]">
                        {t('labels.period')}
                    </span>
                    <span ref={datepickerContainerRef}>
                        <Datepicker
                            i18n={datepickerLocale}
                            startWeekOn={weekStart}
                            primaryColor={'yellow'}
                            value={searchPeriod}
                            onChange={(value) => {
                                if (!isPaid && value?.startDate && value?.endDate) {
                                    // 有料プラン用の期間が選択されたかチェック
                                    const periodType = getPaidPlanPeriodType(
                                        value.startDate as Date,
                                        value.endDate as Date,
                                    );
                                    if (periodType) {
                                        // モーダルを表示して期間をリセット
                                        setSelectedPeriodType(periodType);
                                        setIsPeriodLockedModalOpen(true);
                                        return;
                                    }
                                }
                                setSearchPeriod(value);
                            }}
                            containerClassName="w-full text-sm border-2 border-lightest-gray rounded-lg max-w-[230px] min-w-[240px] lg:min-w-[220px]"
                            inputClassName="py-2 px-3 rounded-lg focus:outline-none whitespace-normal w-full overflow-visible"
                            toggleClassName="hidden"
                            showShortcuts={true}
                            // ショートカット設定
                            configs={{
                                shortcuts: getDatepickerShortcuts(
                                    isPaid,
                                    shortcutLabels,
                                    { weekStartsOn: weekStart },
                                ),
                            }}
                        />
                    </span>
                </div>
                {/* 色選択（暫定） */}
                <div className="flex flex-wrap items-center">
                    <p className="text-gray-500 shrink-0 mb-0 lg:mb-0 mr-2 pb-1 min-w-[100px] lg:min-w-[10px]">
                        {t('labels.color')}
                    </p>
                    <Select
                        options={colorOptionsView}
                        defaultValue={selectedColorOption}
                        onValueChange={(option) => {
                            // 仕切りは選択不可
                            if ((option as any).isDivider) return;
                            setSearchColor(localizeColorOption(option));
                            // MRU保存: 個別色のみ（''/all/仕切り除外）
                            const v = option?.value;
                            if (v && v !== 'all') {
                                (async () => {
                                    const userId = userInfo?.userId || '';
                                    const key = `${LocalStorageKeys.COLOR_ORDER}:${searchCalendar.value}`;
                                    const saved = await getLocalStorage(userId, key);
                                    let order: string[] = [];
                                    if (saved) {
                                        try {
                                            order = JSON.parse(saved);
                                        } catch {}
                                    }
                                    // 先頭に移動（重複排除）
                                    order = [v, ...order.filter((x) => x !== v)].slice(0, 20);
                                    await setLocalStorage(userId, key, JSON.stringify(order));
                                    // 再描画用にリスト更新
                                    await refreshColorOptions(searchCalendar.value);
                                })();
                            }
                        }}
                        className="text-base w-full min-w-[250px]"
                        dropdownClassName="min-w-[500px] w-[500px]"
                    />
                </div>
                {/* キーワード入力 */}
                <div className="flex flex-wrap items-center">
                    <span className="text-gray-500 shrink-0 mb-0 lg:mb-0 mr-2 pb-1 min-w-[150px] lg:min-w-[10px]">
                        {t('labels.keyword')}
                    </span>
                    <span>
                        {/** 色が「すべて」の場合は入力不可 */}
                        {(() => {
                            const isAllColor = searchColor?.value === 'all';
                            return (
                                <Input
                                    className="border-2 border-lightest-gray rounded-lg focus-visible:border-white"
                                    placeholder={
                                        isAllColor
                                            ? t('placeholders.keywordDisabled')
                                            : t('placeholders.keywordExample')
                                    }
                                    error={false}
                                    helperText=""
                                    showClearButton={!isAllColor}
                                    name="searchKeyword"
                                    value={searchKeyword}
                                    disabled={isAllColor}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (isAllColor) return;
                                        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                            handleSubmit();
                                        }
                                    }}
                                />
                            );
                        })()}
                    </span>
            </div>
            {/* 右側: 分析するボタン（常に右寄せ・左側の空きに合わせて広がる） */}
            <div className="flex items-center justify-center w-full lg:w-auto lg:basis-1/5 lg:flex-shrink-0 lg:min-w-[180px] mt-2 lg:mt-0">
                <Button
                    variant="original"
                    onClick={handleSubmit}
                    className={`w-auto px-6 gap-1 ${isFirstSearch ? 'animate-beat hover:paused' : ''}`}
                >
                    <span className="text-md pb-0.5 tracking-[0.1em]">{t('button.analyze')}</span>
                    <img
                        src={magnifyingGlass}
                        alt={t('button.analyzeAlt')}
                        className="w-4 h-4 ml-1"
                    />
                </Button>
            </div>
        </div>
            </div>

            {!isPaidPlan && (
                <PlanLockedModal
                    isOpen={isPlanModalOpen}
                    onClose={() => setIsPlanModalOpen(false)}
                    onGoPlans={() =>
                        openPlansWithTracking({
                            source: 'calendar_selection_analysis',
                        })
                    }
                />
            )}
            {/* 期間選択ロックモーダル */}
            <PeriodLockedModal
                isOpen={isPeriodLockedModalOpen}
                onClose={() => setIsPeriodLockedModalOpen(false)}
                onGoPlans={() => {
                    setIsPeriodLockedModalOpen(false);
                    const periodSource = selectedPeriodType
                        ? `period_selection_modal_${selectedPeriodType}`
                        : 'period_selection_modal';
                    openPlansWithTracking({
                        source: periodSource,
                        panelId: 'anSearch',
                    });
                }}
            />
        </Fragment>
    );
};

export default Search;
